import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { Button, Grid, Popover, Stack, Typography } from '@mui/material';
import { authContext } from '@staffedupusa/shared-components';
import { FC, MouseEvent, useContext, useEffect, useState } from 'react';
import XIcon from '@mui/icons-material/X';
import { facebookBlue, linkedInBlue, twitterBlue } from '@styles/colors';
import { useGetMetrics } from '@features/enterpriseMetrics/hooks/useGetSavedMessages';
import { selectedPlanName } from '@utils/pricing';
import { track } from '@amplitude/analytics-browser';
import { amplitudeEventsName } from '@utils/amplitudeEvents';

type BoostModalContentProps = {
  handleRequestSocialClick: () => void;
  handleRequestLocationClick: () => void;
  handlePostToSocialMediaClick: () => void;
  selectedCompanyId?: string | number;
};

const BoostModalContent: FC<BoostModalContentProps> = ({
  handleRequestSocialClick,
  handleRequestLocationClick,
  handlePostToSocialMediaClick,
  selectedCompanyId,
}) => {
  const { authData } = useContext(authContext);
  const isCompany = authData.user?.company;
  const selectedPlan = selectedPlanName(authData?.user?.company?.plan || 0);

  const [popoverAnchor, setPopoverAnchor] = useState<HTMLButtonElement | null>(
    null,
  );
  const [selectedComany, seSelectedCompany] = useState(null);
  const { metricsData, isLoadingMetrics } = useGetMetrics({
    enterprise_id: authData?.user?.enterprise?.id,
    selectedCompanyId: 'ALL',
  });

  useEffect(() => {
    if (selectedCompanyId && metricsData) {
      const selectCompany = metricsData?.companies?.find(
        (item) => item.company_id == selectedCompanyId,
      );
      seSelectedCompany(selectCompany ?? null);
    }
  }, [metricsData]);

  const handleShareMyLink = (event: MouseEvent<HTMLButtonElement>) => {
    isCompany &&
      track(
        `${amplitudeEventsName.APPLICANTS_CLICK_SHARE_HIRING}-${selectedPlan}`,
      );
    navigator.clipboard.writeText(
      `${import.meta.env.VITE_MARKETING_SITE_URL}${
        authData.user?.company
          ? authData.user?.company?.s_slug
          : authData.user?.enterprise && selectedCompanyId && selectedComany
            ? //@ts-ignore
              selectedComany.s_slug
            : ''
      }?src=social`,
    );

    setPopoverAnchor(event.currentTarget);

    setTimeout(() => {
      setPopoverAnchor(null);
    }, 2000);
  };

  return (
    <Grid container columnSpacing={1} rowSpacing={2} color="lightPurple.main">
      <Grid
        item
        xs={12}
        sm={6}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Button
          variant="contained"
          color="lightPurple"
          onClick={handleShareMyLink}
          sx={{ width: '80%' }}
        >
          Share My Hiring link
        </Button>
        <Popover
          open={Boolean(popoverAnchor)}
          anchorEl={popoverAnchor}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          onClose={() => setPopoverAnchor(null)}
          disablePortal
        >
          <Typography p={2}>Hiring link copied to clipboard</Typography>
        </Popover>
      </Grid>
      <Grid item xs={12} sm={6} display="flex" alignItems="center">
        <Typography variant="body1">
          Copy your StaffedUp hiring url and share it via email, text,
          newsletters, or anywhere you want to get eye on it.
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        sm={6}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Button
          variant="contained"
          color="lightPurple"
          onClick={handlePostToSocialMediaClick}
          sx={{ width: '80%' }}
        >
          Post my jobs <br /> to social media
        </Button>
      </Grid>
      <Grid
        item
        xs={12}
        sm={6}
        display="flex"
        alignItems="center"
        flexDirection="column"
        justifyContent="center"
      >
        <Typography variant="body1">
          Post directly to Facebook, Linkedin, or X with just a few clicks.
        </Typography>
        <Stack
          spacing={1}
          direction="row"
          alignItems="center"
          justifyContent="center"
        >
          <FacebookIcon sx={{ color: facebookBlue }} />
          <LinkedInIcon sx={{ color: linkedInBlue }} />
          <XIcon sx={{ color: '#000', fontSize: 20 }} />
        </Stack>
      </Grid>
      <Grid
        item
        xs={12}
        sm={6}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Button
          onClick={handleRequestSocialClick}
          color="green"
          variant="contained"
          sx={{ width: '80%' }}
        >
          Request a Social Boost
        </Button>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="body1" display="flex" alignItems="center">
          We'll post to our networks on Linkedin, Facebook, X, & Instagram and
          share to any job groups in your area.
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        sm={6}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Button
          onClick={handleRequestLocationClick}
          color="green"
          variant="contained"
          sx={{ width: '80%' }}
        >
          Request a Location Boost
        </Button>
      </Grid>
      <Grid item xs={12} sm={6} display="flex" alignItems="center">
        <Typography variant="body1">
          Get your jobs featured at the top of every job search in your area.
        </Typography>
      </Grid>
    </Grid>
  );
};

export default BoostModalContent;
